/**
 * Ad-attribution URL parameters.
 *
 * When a user lands on ssfdetail.com from a Google Ads / Meta Ads / other paid
 * campaign, the ad platform appends parameters to the URL (?gclid=... for
 * Google Ads, ?gbraid= / ?wbraid= for iOS privacy-safe variants, ?fbclid= for
 * Meta, plus utm_* for GA/GTM).
 *
 * We capture these on each URL change and stash them in sessionStorage so
 * that any subsequent form submission (contact form, LP form, Stripe checkout)
 * can forward them to the backend, where they get saved with the booking. This
 * enables **Google Ads Offline Conversions**: when a booked job ships revenue,
 * you can upload a CSV of (gclid, revenue, conversion_time) to Google Ads and
 * it attributes the money back to the exact click that produced it — the
 * single biggest lever for Smart Bidding accuracy.
 *
 * SessionStorage (not localStorage) is intentional: attribution only lasts for
 * the current session so a returning user who typed the URL directly isn't
 * mis-attributed to an old click.
 */

const KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid',
              'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
              'gad_source'];
const STORAGE_KEY = 'ssf_attribution';
export const GOOGLE_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'];
let memoryAttribution = {};
let storageWriteFailed = false;

/**
 * Read attribution params from URL and stash in sessionStorage (if any present).
 * Called on initial landing and SPA URL changes from App.js.
 */
export const captureAttributionFromUrl = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    for (const k of KEYS) {
      const v = params.get(k);
      if (v) captured[k] = v;
    }
    if (Object.keys(captured).length === 0) return;

    // Merge with existing so a later click's utm_ doesn't wipe an earlier gclid.
    const existing = readAttribution();
    const merged = { ...existing, ...captured, first_seen_at: existing.first_seen_at || new Date().toISOString(), last_landing_url: window.location.pathname + window.location.search };
    memoryAttribution = merged;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      storageWriteFailed = false;
    } catch (_) {
      // Keep same-page/SPA navigation working when storage is blocked or full.
      storageWriteFailed = true;
    }
  } catch (_) {
    /* Attribution must never prevent a visitor from using a quote form. */
  }
};

/**
 * Read the currently-stored attribution blob. Returns {} if none.
 */
export const readAttribution = () => {
  if (typeof window === 'undefined') return {};
  if (storageWriteFailed) return { ...memoryAttribution };
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries([...KEYS, 'first_seen_at', 'last_landing_url']
      .filter((key) => typeof parsed[key] === 'string')
      .map((key) => [key, parsed[key]]));
  } catch (_) {
    return { ...memoryAttribution };
  }
};

/** Current URL wins only when nonempty; IDs remain opaque and case-sensitive. */
export const getGoogleClickIds = (search) => {
  const stored = readAttribution();
  const params = new URLSearchParams(search ?? (typeof window === 'undefined' ? '' : window.location.search));
  return Object.fromEntries(GOOGLE_CLICK_ID_KEYS.map((key) => [key, params.get(key) || stored[key] || '']));
};

/** Submit the actual named hidden controls, rather than relying on storage alone. */
export const getGoogleClickIdsFromForm = (formData) => Object.fromEntries(
  GOOGLE_CLICK_ID_KEYS.map((key) => {
    const value = formData.get(key);
    return [key, typeof value === 'string' ? value : ''];
  }),
);
